<template>
	<div class="course-detail-page">
		<div class="main-content">
			<!-- Main Content Layout -->
			<div class="player-container">
				<!-- Video Area (Left Side) -->
				<div class="video-wrapper">
					<!-- Video Player -->
					<div class="video-player" @mouseenter="showControls = true" @mouseleave="hideControlsWithDelay"
						ref="playerContainer">
						<video ref="videoRef" class="real-video" :poster="courseInfo.cover" @click="togglePlay"
							@timeupdate="updateTime" @loadedmetadata="onLoadedMetadata" @ended="onEnded">
							<source :src="currentEpisode.videoFile" type="video/mp4">
							Your browser does not support the video tag.
						</video>

						<!-- Loading Spinner (Optional) -->
						<div v-if="loading" class="loading-spinner">
							<div class="spinner"></div>
						</div>

						<!-- Member Access Overlay -->
						<div v-if="showMemberOverlay" class="member-overlay">
							<div class="overlay-content">
								<h3>试看已结束</h3>
								<button v-if="lockType === 'vip'" class="vip-btn" @click="goToMember">开通会员继续观看</button>
								<button v-else-if="lockType === 'paid'" class="vip-btn"
									@click="handleBuy">购买课程继续观看</button>
							</div>
						</div>

						<!-- Top Overlay (Title) -->
						<transition name="fade">
							<div class="video-top-bar" v-show="showControls">
								<h2 class="video-title">{{ currentEpisode.title || courseInfo.title }}</h2>
							</div>
						</transition>

						<!-- Bottom Controls Overlay -->
						<transition name="fade">
							<div class="video-controls-bar" v-show="showControls">
								<!-- Progress Bar -->
								<div class="progress-container" @mousedown="startDrag" @click="seekByClick">
									<div class="progress-bg"></div>
									<div class="progress-buffered" :style="{ width: bufferedPercentage + '%' }"></div>
									<div class="progress-current" :style="{ width: progressPercentage + '%' }">
										<div class="progress-handle"></div>
									</div>
								</div>

								<div class="controls-row">
									<!-- Left Controls -->
									<div class="left-controls">
										<button class="control-btn play-btn" @click="togglePlay">
											<svg v-if="!isPlaying" viewBox="0 0 24 24" width="24" height="24">
												<path fill="currentColor" d="M8 5v14l11-7z" />
											</svg>
											<svg v-else viewBox="0 0 24 24" width="24" height="24">
												<path fill="currentColor" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
											</svg>
										</button>
										<span class="time-display">
											{{ formatTime(currentTime) }} / {{ formatTime(duration) }}
										</span>
									</div>

									<!-- Right Controls -->
									<div class="right-controls">
										<!-- Volume -->
										<div class="volume-control" @mouseenter="isVolumeHover = true"
											@mouseleave="isVolumeHover = false">
											<button class="control-btn" @click="toggleMute">
												<svg v-if="volume === 0" viewBox="0 0 24 24" width="24" height="24">
													<path fill="currentColor"
														d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73 4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
												</svg>
												<svg v-else viewBox="0 0 24 24" width="24" height="24">
													<path fill="currentColor"
														d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
												</svg>
											</button>
											<div class="volume-slider-container" v-show="isVolumeHover">
												<div class="volume-slider-bg" @click="setVolumeByClick">
													<div class="volume-slider-current"
														:style="{ height: (volume * 100) + '%' }"></div>
												</div>
											</div>
										</div>

										<!-- Custom Actions based on Image -->
										<button class="control-text-btn" @click="playPrev"
											:disabled="currentEpisodeIndex === 0"
											:class="{ disabled: currentEpisodeIndex === 0 }">
											上一集
										</button>
										<button class="control-text-btn" @click="playNext"
											:disabled="currentEpisodeIndex === episodeList.length - 1"
											:class="{ disabled: currentEpisodeIndex === episodeList.length - 1 }">
											下一集
										</button>

										<!-- Speed Selector -->
										<div class="speed-control">
											<button class="control-text-btn" @click.stop="showSpeedMenu = !showSpeedMenu">
												{{ playbackRate === 1 ? '倍速' : playbackRate + 'x' }}
											</button>
											<div class="speed-menu" v-show="showSpeedMenu" @click.stop>
												<div v-for="rate in [2.0, 1.5, 1.25, 1.0, 0.75]" :key="rate"
													class="speed-item" :class="{ active: playbackRate === rate }"
													@click="setSpeed(rate)">
													{{ rate }}x
												</div>
											</div>
										</div>

										<!-- <button class="control-text-btn" @click="toggleSidebar">
											选集
										</button> -->

										<button class="control-btn fullscreen-btn" @click="toggleFullscreen">
											<svg v-if="!isFullscreen" viewBox="0 0 24 24" width="24" height="24">
												<path fill="currentColor"
													d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
											</svg>
											<svg v-else viewBox="0 0 24 24" width="24" height="24">
												<path fill="currentColor"
													d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-14v3h3v2h-5V5z" />
											</svg>
										</button>
									</div>
								</div>
							</div>
						</transition>
					</div>

					<!-- Video Footer Info (Removed buttons as they are now in the player) -->
				</div>

				<!-- Sidebar (Right Side) -->
				<div class="sidebar">
					<div class="course-header">
						<!-- Price -->
						<div class="course-price" v-if="courseInfo.price > 0">
							<span class="currency">¥</span>
							<span class="amount">{{ courseInfo.price }}</span>
						</div>

						<h1 class="course-title">{{ courseInfo.title }}</h1>

						<div class="course-meta">
							<span class="badge"
								:class="getBadgeType(courseInfo.viewType)">{{ getBadgeText(courseInfo.viewType) }}</span>
							<p class="course-intro">简介：{{ courseInfo.description }}</p>
						</div>

						<div class="course-actions">
							<button class="action-button star" :class="{ active: isCollect }" @click="toggleFavorite">
								<span class="icon">★</span> {{ isCollect ? '已收藏' : '收藏' }}
							</button>
							<span class="divider">|</span>
							<button class="action-button share" @click="copyShareLink">
								<span class="icon">
									<img src="../assets/icon/shareIcon.png" alt="" style="width: 14px;height: 14px;" />
								</span> {{ shareCopied ? '已复制' : '分享' }}
							</button>
						</div>

						<!-- Buy Button -->
						<button v-if="showBuyButton" class="buy-now-btn" @click="handleBuy">
							¥ {{ courseInfo.price }} 立即购买
						</button>
					</div>

					<!-- Directory -->
					<div class="directory-section">
						<h3>目录</h3>
						<div class="episode-grid">
							<div v-for="(episode, index) in episodeList" :key="episode.id" class="episode-item"
								:class="{ active: currentEpisodeIndex === index }" @click="playEpisode(index, true)">
								<div v-if="currentEpisodeIndex === index" class="playing-bars">
									<span></span><span></span><span></span>
								</div>
								<span v-else>{{ episode.title }}</span>
								<img v-if="(courseInfo.viewType == 2 && !isMember || courseInfo.viewType == 3 && !courseInfo.havePay) && episode.isTry != 1"
									src="../assets/icon/membershipLock.png" class="lock-icon" />
							</div>
						</div>
					</div>

					<!-- Recommendations -->
					<div class="recommend-section">
						<div class="section-divider">
							<span class="line"></span>
							<span class="text">猜你喜欢</span>
							<span class="line"></span>
						</div>
						<div class="recommend-grid">
							<div v-for="item in recommendList" :key="item.id" class="recommend-card"
								@click="goToCourse(item.courseId || item.id)">
								<div class="card-cover"
									:style="{ backgroundImage: `url(${item.coverImg || item.cover})` }">
									<span class="badge vip" v-if="item.viewType == 2">VIP</span>
									<span class="badge paid" v-else-if="item.viewType == 1">免费</span>
									<span class="badge paid" v-else-if="item.viewType == 3">付费</span>
								</div>
								<div class="card-info">
									<h4>{{ item.title }}</h4>
									<p>{{ formatSales(item.sales || 0) }}人浏览</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<!-- Payment Modal -->
		<div v-if="showPaymentModal" class="modal-overlay" @click.self="closePaymentModal">
			<div class="payment-modal">
				<div class="modal-header">
					<h3>确认订单</h3>
					<button class="close-modal-btn" @click="closePaymentModal">×</button>
				</div>

				<div class="modal-product">
					<img :src="courseInfo.cover" class="modal-cover" />
					<div class="modal-product-info">
						<h4 class="modal-product-title">{{ courseInfo.title }}</h4>
						<p class="modal-product-price">¥ {{ courseInfo.price }}</p>
					</div>
				</div>

				<div class="payment-methods">
					<div class="methods-label">支付方式</div>

					<div class="method-item" @click="paymentMethod = 'wallet'">
						<div class="method-left">
							<span class="method-icon wallet">
								<img src="../assets/icon/walletIcon.png" alt="" style="width: 30px;height: 30px;" />
							</span>
							<span class="method-name">钱包支付</span>
						</div>
						<div class="radio-circle" :class="{ checked: paymentMethod === 'wallet' }">
							<div class="radio-inner" v-if="paymentMethod === 'wallet'"></div>
						</div>
					</div>

					<div class="method-item" @click="paymentMethod = 'wechat'">
						<div class="method-left">
							<span class="method-icon wechat">
								<img src="../assets/icon/wechatIcon.png" alt="" style="width: 30px;height: 30px;" />
							</span>
							<span class="method-name">微信支付</span>
						</div>
						<div class="radio-circle" :class="{ checked: paymentMethod === 'wechat' }">
							<div class="radio-inner" v-if="paymentMethod === 'wechat'"></div>
						</div>
					</div>
				</div>

				<button class="confirm-pay-btn" @click="confirmPayment">立即购买</button>
			</div>
		</div>

		<!-- WeChat Pay QR Modal -->
		<div class="modal-overlay" v-if="showPayModal" @click.self="showPayModal = false">
			<div class="payment-modal">
				<div class="modal-header">
					<h3>微信支付</h3>
					<button class="close-modal-btn" @click="showPayModal = false">×</button>
				</div>
				<div class="modal-product" v-if="courseInfo">
					<img :src="courseInfo.cover" class="modal-cover" />
					<div class="modal-product-info">
						<h4 class="modal-product-title">{{ courseInfo.title }}</h4>
						<p class="modal-product-price">¥ {{ courseInfo.price }}</p>
					</div>
				</div>
				<div class="payment-methods">
					<transition name="qr-fade" mode="out-in">
						<div class="qr-container" v-if="payQrCode" key="qr">
							<img :src="payQrCode" alt="Payment QR Code" />
							<p>请使用微信扫一扫支付</p>
						</div>
						<div class="loading" v-else key="loading">
							<span class="spinner"></span>
							<p>正在生成支付二维码...</p>
						</div>
					</transition>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import ProductAPI from '@/api/product'
	import {
		createOrder,
		miniAppPay
	} from '@/api/order'
	import {
		toggleFavorite,
		getUserProfile
	} from '@/api/user'
	import eventBus from '@/utils/eventBus'
	import {
		ElMessage
	} from 'element-plus'
	import 'element-plus/es/components/message/style/css'

	export default {
		name: 'CourseDetail',
		data() {
			return {
				courseId: null,
				isCollect: false,
				isMember: false,
				showMemberOverlay: false,
				lockType: '', // vip or paid
				courseInfo: {
					title: '',
					description: '',
					cover: ''
				},
				lecturer: {
					name: '',
					avatar: ''
				},
				currentEpisodeIndex: 0,
				episodeList: [],
				recommendList: [],
				// Video Player State
				isPlaying: false,
				currentTime: 0,
				duration: 0,
				volume: 1,
				playbackRate: 1.0,
				showControls: true,
				showSpeedMenu: false,
				isVolumeHover: false,
				isFullscreen: false,
				loading: false,
				controlTimeout: null,
				bufferedPercentage: 0,
				// Payment Modal State
				showPaymentModal: false,
				paymentMethod: 'wallet', // 'wallet', 'wechat'
				shareCopied: false,
				// WeChat Pay QR Modal
				showPayModal: false,
				payQrCode: ''
			}
		},
		computed: {
			currentEpisode() {
				return this.episodeList[this.currentEpisodeIndex] || {};
			},
			isLoggedIn() {
				return !!localStorage.getItem('token');
			},
			progressPercentage() {
				if (!this.duration) return 0;
				return (this.currentTime / this.duration) * 100;
			},
			showBuyButton() {
				// 付费课程(3)且未购买，或者有价格且未购买
				return (this.courseInfo.viewType == 3 && !this.courseInfo.havePay) ||
					(this.courseInfo.price > 0 && !this.courseInfo.havePay);
			}
		},
		async created() {
			this.courseId = this.$route.params.id;
			await this.checkMemberStatus();
			this.fetchCourseDetails();
			this.fetchRecommendations();
		},
		beforeUnmount() {
			if (this.controlTimeout) clearTimeout(this.controlTimeout);
			window.removeEventListener('keydown', this.handleKeydown);
			document.removeEventListener('fullscreenchange', this.onFullscreenChange);
		},
		mounted() {
			// 监听键盘事件与全屏状态变化
			window.addEventListener('keydown', this.handleKeydown);
			document.addEventListener('fullscreenchange', this.onFullscreenChange);
		},
		methods: {
			async checkMemberStatus() {
				try {
					const res = await getUserProfile();
					if (res && res.isMember == 1) {
						this.isMember = true;
					} else {
						this.isMember = false;
					}
				} catch (error) {
					console.error('Failed to check member status:', error);
					this.isMember = false;
				}
			},
			goToMember() {
				this.$router.push('/member');
			},
			copyShareLink() {
				const url = window.location.href;
				navigator.clipboard.writeText(url).then(() => {
					this.shareCopied = true;
					ElMessage.success('链接已复制');
					setTimeout(() => this.shareCopied = false, 2000);
				}).catch(() => {
					ElMessage.error('复制失败');
				});
			},
			// ... (keep existing fetch methods)
			async fetchRecommendations() {
				try {
					const res = await ProductAPI.getCourseList({
						page: 1,
						pageSize: 4
					});
					if (res && res.rows) {
						this.recommendList = res.rows;
					} else if (Array.isArray(res)) {
						this.recommendList = res;
					}
				} catch (error) {
					console.error('Failed to fetch recommendations:', error);
				}
			},
			async fetchCourseDetails() {
				try {
					const res = await ProductAPI.getPublicCourseDetail(this.courseId);

					const data = res; // Adjust based on actual API response structure

					this.courseInfo = {
						title: data.title,
						description: data.intro || data.description,
						cover: data.coverImg,
						viewType: data.viewType,
						isCollect: data.isCollect,
						havePay: data.havePay,
						price: data.price || 0
					};

					if (data.teacher) {
						this.lecturer = {
							name: data.teacher.name,
							avatar: data.teacher.avatar
						};
					} else {
						this.lecturer = {
							name: data.teacherName,
							avatar: data.teacherAvatar
						};
					}

					this.episodeList = data.chapters || data.episodeList || [];

					// 默认选中第一集并尝试播放（如果不受限）
					if (this.episodeList.length > 0) {
						this.playEpisode(0);
					}

					if (typeof data.isCollect !== 'undefined') {
						this.isCollect = data.isCollect;
					}

				} catch (error) {
					console.error('Failed to fetch course details:', error);
				}
			},
			// Player Methods
			togglePlay() {
				const video = this.$refs.videoRef;
				if (!video) return;

				// Check restrictions based on reference logic
				const episode = this.episodeList[this.currentEpisodeIndex];

				const needVip = this.courseInfo.viewType == 2 && !this.isMember;
				const needPay = this.courseInfo.viewType == 3 && !this.courseInfo.havePay;

				if (needVip || needPay) {
					// isTry==1 时当前章节支持试看，不区分付费还是 VIP，允许播放
					if (episode && episode.isTry != 1) {
						this.lockType = needVip ? 'vip' : 'paid';
						this.showMemberOverlay = true;
						return;
					}
					// 支持试看但无视频源时仍显示 overlay
					if (!episode.videoFile) {
						this.lockType = needVip ? 'vip' : 'paid';
						this.showMemberOverlay = true;
						return;
					}
				}

				if (video.paused) {
					video.play().then(() => {
						this.isPlaying = true;
					}).catch(err => {
						console.error("Play failed:", err);
					});
				} else {
					video.pause();
					this.isPlaying = false;
				}
			},
			updateTime(e) {
				const video = e.target;
				this.currentTime = video.currentTime;
				if (video.buffered.length > 0) {
					try {
						const bufferedEnd = video.buffered.end(video.buffered.length - 1);
						if (this.duration > 0) {
							this.bufferedPercentage = (bufferedEnd / this.duration) * 100;
						}
					} catch (e) {
						// Ignore buffer errors
					}
				}
			},
			onLoadedMetadata(e) {
				this.duration = e.target.duration;
				this.loading = false;
			},
			onEnded() {
				this.isPlaying = false;

				const episode = this.episodeList[this.currentEpisodeIndex];
				if (!episode) return;

				const needVip = this.courseInfo.viewType == 2 && !this.isMember;
				const needPay = this.courseInfo.viewType == 3 && !this.courseInfo.havePay;

				// 如果无权限
				if (needVip || needPay) {
					// 播放结束后，无论是不是试看章节，都弹出购买提示（试看结束引导购买）
					this.lockType = needVip ? 'vip' : 'paid';
					this.showMemberOverlay = true;
					return;
				}

				// Auto play next logic
				if (this.currentEpisodeIndex < this.episodeList.length - 1) {
					// Optional: Auto play next
					// this.playNext();
				}
			},
			playEpisode(index, isUserAction = false) {
				const episode = this.episodeList[index];
				if (!episode) return;

				if (isUserAction && !this.isLoggedIn) {
					ElMessage.warning('请先登录');
					eventBus.emit('show-login');
					return;
				}

				this.currentEpisodeIndex = index;
				this.showMemberOverlay = false;
				this.isPlaying = false;
				this.lockType = '';

				// Check access rights before playing
				const needVip = this.courseInfo.viewType == 2 && !this.isMember;
				const needPay = this.courseInfo.viewType == 3 && !this.courseInfo.havePay;

				if (needVip || needPay) {
					// isTry==1 时当前章节支持试看，不区分付费还是 VIP，允许播放
					if (episode.isTry != 1) {
						this.lockType = needVip ? 'vip' : 'paid';
						this.showMemberOverlay = true;
						return;
					}
					// 支持试看但无视频源时仍拦截
					if (!episode.videoFile) {
						this.lockType = needVip ? 'vip' : 'paid';
						this.showMemberOverlay = true;
						return;
					}
				}

				this.$nextTick(() => {
					const video = this.$refs.videoRef;
					if (video) {
						video.load();
						// 自动播放需要用户交互，如果在 created/mounted 中调用可能会被浏览器拦截
						// 这里尝试自动播放，如果失败则静默（用户需要手动点击播放）
						video.play().then(() => {
							this.isPlaying = true;
						}).catch(err => {
							console.log('Autoplay prevented', err);
							this.isPlaying = false;
						});
					}
				});
			},
			playPrev() {
				if (this.currentEpisodeIndex > 0) {
					this.playEpisode(this.currentEpisodeIndex - 1, true);
				}
			},
			playNext() {
				if (this.currentEpisodeIndex < this.episodeList.length - 1) {
					this.playEpisode(this.currentEpisodeIndex + 1, true);
				}
			},
			toggleSidebar() {
				// Implementation depends on if sidebar is collapsible, or just scroll to list
				const el = document.querySelector('.directory-section');
				if (el) el.scrollIntoView({
					behavior: 'smooth'
				});
			},
			toggleMute() {
				const video = this.$refs.videoRef;
				if (this.volume > 0) {
					this.lastVolume = this.volume;
					this.volume = 0;
					video.volume = 0;
				} else {
					this.volume = this.lastVolume || 1;
					video.volume = this.volume;
				}
			},
			setVolumeByClick(e) {
				const rect = e.target.closest('.volume-slider-bg').getBoundingClientRect();
				const pos = (rect.bottom - e.clientY) / rect.height;
				this.volume = Math.max(0, Math.min(1, pos));
				this.$refs.videoRef.volume = this.volume;
			},
			setSpeed(rate) {
				this.playbackRate = rate;
				this.$refs.videoRef.playbackRate = rate;
				this.showSpeedMenu = false;
			},
			onFullscreenChange() {
				// 同步 isFullscreen 状态，处理用户按 ESC/F11 退出全屏等情况
				this.isFullscreen = !!document.fullscreenElement;
			},
			handleKeydown(e) {
				const video = this.$refs.videoRef;
				if (!video) return;

				// 仅在全屏或视频元素获得焦点时响应键盘
				const isInFullscreen = this.isFullscreen || document.fullscreenElement === this.$refs.playerContainer;
				const isVideoFocused = document.activeElement === video;

				if (!isInFullscreen && !isVideoFocused) return;

				switch (e.code) {
					case 'Space':
						e.preventDefault();
						this.togglePlay();
						break;
					case 'ArrowRight':
						e.preventDefault();
						{
							const target = (video.currentTime || 0) + 5;
							const maxDuration = this.duration || video.duration || target;
							video.currentTime = Math.min(target, maxDuration);
							this.currentTime = video.currentTime;
						}
						break;
					case 'ArrowLeft':
						e.preventDefault();
						{
							const target = (video.currentTime || 0) - 5;
							video.currentTime = Math.max(target, 0);
							this.currentTime = video.currentTime;
						}
						break;
					case 'ArrowUp':
						e.preventDefault();
						{
							const base = typeof this.volume === 'number' ? this.volume : (video.volume || 0);
							this.volume = Math.min(1, base + 0.1);
							video.volume = this.volume;
						}
						break;
					case 'ArrowDown':
						e.preventDefault();
						{
							const base = typeof this.volume === 'number' ? this.volume : (video.volume || 0);
							this.volume = Math.max(0, base - 0.1);
							video.volume = this.volume;
						}
						break;
					default:
						return;
				}

				// 键盘操作时显示控制栏，并按原逻辑延时隐藏
				this.showControls = true;
				if (this.controlTimeout) {
					clearTimeout(this.controlTimeout);
					this.controlTimeout = null;
				}
				this.hideControlsWithDelay();
			},
			toggleFullscreen() {
				const container = this.$refs.playerContainer;
				if (!document.fullscreenElement) {
					container.requestFullscreen().catch(err => {
						console.error(`Error attempting to enable full-screen mode: ${err.message}`);
					});
					this.isFullscreen = true;
				} else {
					document.exitFullscreen();
					this.isFullscreen = false;
				}
			},
			seekByClick(e) {
				const rect = e.currentTarget.getBoundingClientRect();
				const pos = (e.clientX - rect.left) / rect.width;
				this.currentTime = pos * this.duration;
				this.$refs.videoRef.currentTime = this.currentTime;
			},
			formatTime(seconds) {
				if (!seconds || isNaN(seconds)) return '00:00';
				const m = Math.floor(seconds / 60);
				const s = Math.floor(seconds % 60);
				return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
			},
			hideControlsWithDelay() {
				if (this.isPlaying) {
					this.controlTimeout = setTimeout(() => {
						this.showControls = false;
					}, 3000);
				}
			},
			// ... existing methods like toggleFavorite, goToCourse
			async toggleFavorite() {
				if (!this.isLoggedIn) {
					ElMessage.warning('请先登录');
					eventBus.emit('show-login'); // Trigger login modal
					return;
				}
				try {
					await toggleFavorite({
						courseId: this.courseId
					})
					this.isCollect = !this.isCollect;
					ElMessage.success(this.isCollect ? '收藏成功' : '已取消收藏');
				} catch (error) {
					ElMessage.error('操作失败');
				}
			},
			async handleBuy() {
				if (!this.isLoggedIn) {
					ElMessage.warning('请先登录');
					eventBus.emit('show-login');
					return;
				}
				this.showPaymentModal = true;
			},
			closePaymentModal() {
				this.showPaymentModal = false;
			},
			// 支付成功后刷新课程权限与页面状态
			async checkCourseAccess() {
				// 重新获取课程详情（会更新 havePay、章节等信息）
				await this.fetchCourseDetails();
				// 关闭试看/购买遮罩
				this.showMemberOverlay = false;
				this.lockType = '';
			},
			async confirmPayment() {
				try {
					if (this.paymentMethod === 'wallet') {
						const createRes = await createOrder({
							courseId: this.courseId,
							goodsType: 1, // 1: Course, 2: Member
							payType: 2 // Wallet
						});
						const ordersId = createRes.orderId;
						const payRes = await miniAppPay({
							ordersId: ordersId,
							payType: 2 // 余额支付
						});
						if (payRes) {
							ElMessage.success('购买成功');
							this.showPaymentModal = false;
							this.checkCourseAccess(); // Refresh access status
						}
					} else {
						// WeChat Pay - 使用二维码扫码支付
						const createRes = await createOrder({
							courseId: this.courseId,
							goodsType: 1, // 1: Course
							payType: 3 // WeChat QR (PC)
						});

						const ordersId = createRes.orderId;

						// 显示二维码弹窗并重置二维码
						this.showPayModal = true;
						this.payQrCode = '';
						// 关闭订单确认弹窗，避免重复遮罩
						this.showPaymentModal = false;

						const payRes = await miniAppPay({
							ordersId: ordersId,
							payType: 3
						});

						// 参考 MyWallet.vue 生成二维码
						if (payRes && (payRes.codeUrl || payRes.code_url)) {
							const codeUrl = payRes.codeUrl || payRes.code_url;
							this.payQrCode =
								`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(codeUrl)}`;
						} else if (typeof payRes === 'string' && payRes.startsWith('weixin://')) {
							this.payQrCode =
								`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(payRes)}`;
						} else if (payRes) {
							this.payQrCode = payRes.codeUrl || payRes.qrCode || payRes;
						}
					}
				} catch (error) {
					console.error(error);
					ElMessage.error(error.message || '购买失败');
				}
			},
			formatSales(val) {
				if (!val) return '0';
				if (val > 10000) {
					return (val / 10000).toFixed(1) + '万';
				}
				return val;
			},
			getBadgeType(viewType) {
				// 1:免费, 2:VIP免费, 3:付费
				if (viewType == 2) return 'vip';
				if (viewType == 3) return 'paid';
				return 'free';
			},
			getBadgeText(viewType) {
				// 1:免费, 2:VIP免费, 3:付费
				if (viewType == 2) return 'VIP';
				if (viewType == 3) return '付费';
				return '免费';
			},
			goToCourse(id) {
				this.$router.push({
					name: 'CourseDetail',
					params: {
						id
					}
				});
				this.courseId = id;
				this.fetchCourseDetails();
				window.scrollTo(0, 0);
			}
		},
		watch: {
			showControls(val) {
				if (val && this.controlTimeout) {
					clearTimeout(this.controlTimeout);
					this.controlTimeout = null;
				}
			}
		}
	}
</script>

<style scoped>
	.course-detail-page {
		min-height: 100vh;
		background-color: #000;
		/* Darker background for the page */
		color: #ccc;
		padding-top: 20px;
		font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
	}

	.main-content {
		max-width: 1400px;
		margin: 0 auto;
		padding: 0 20px;
	}

	.player-container {
		display: flex;
		gap: 20px;
		height: calc(100vh - 60px);
		/* Fill screen height minus header/padding */
		min-height: 600px;
	}

	/* Sidebar Styling (Right Side) */
	.sidebar {
		width: 320px;
		background-color: #1a1a1a;
		display: flex;
		flex-direction: column;
		border-radius: 8px;
		overflow: hidden;
		flex-shrink: 0;
		/* Prevent shrinking */
	}

	/* Course Header */
	.course-header {
		padding: 20px;
		border-bottom: 1px solid #2a2a2a;
	}

	.course-price {
		margin-bottom: 10px;
		color: #E5C080;
		/* Gold/Orange color */
		line-height: 1;
	}

	.course-price .currency {
		font-size: 18px;
		margin-right: 4px;
	}

	.course-price .amount {
		font-size: 28px;
		font-weight: 500;
	}

	.course-title {
		margin: 0 0 10px 0;
		font-size: 16px;
		color: #fff;
		line-height: 1.4;
		font-weight: 500;
	}

	.course-meta {
		display: flex;
		align-items: flex-start;
		gap: 8px;
		margin-bottom: 16px;
		font-size: 12px;
	}

	.course-meta .badge {
		background: rgba(255, 158, 39, 0.1);
		font-family: PingFang SC, PingFang SC;
		font-weight: 500;
		font-size: 9px;
		color: #FF9E27;
		width: 26px;
		height: 13px;
		border-radius: 2px 2px 2px 2px;
		text-align: center;
		/* 微调对齐 */
	}

	/* .course-meta .badge.free {
		background-color: #333;
		color: #FF9E27;
		border: 1px solid #FF9E27;
	}

	.course-meta .badge.vip {}

	.course-meta .badge.paid {
		background-color: #ff4d4f;
		color: #fff;
	} */

	/* Removed badge styles from header since they are not in the new design */

	.course-intro {
		color: #999;
		margin: 0;
		/* Allow multi-line for intro in sidebar header */
		line-height: 1.5;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
		flex: 1;
		/* Take remaining width */
	}

	.course-actions {
		display: flex;
		align-items: center;
		background-color: #2a2a2a;
		border-radius: 6px;
		padding: 8px 0;
		margin-bottom: 15px;
		/* Space for buy button */
	}

	.buy-now-btn {
		width: 100%;
		background-color: #ff9900;
		border: none;
		border-radius: 20px;
		/* Rounded pill shape */
		padding: 12px 0;
		color: #fff;
		font-size: 16px;
		font-weight: 500;
		cursor: pointer;
		transition: background-color 0.2s;
	}

	.buy-now-btn:hover {
		background-color: #e68a00;
	}

	.action-button {
		flex: 1;
		background: none;
		border: none;
		color: #999;
		cursor: pointer;
		font-size: 13px;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		transition: color 0.2s;
	}

	.action-button:hover {
		color: #fff;
	}

	.action-button.star.active {
		color: #ff9900;
	}

	.action-button .icon {
		font-size: 14px;
	}

	.divider {
		color: #444;
		font-size: 14px;
		line-height: 1;
	}

	/* Directory Section */
	.directory-section {
		padding: 20px;
		flex: 1;
		overflow-y: auto;
	}

	.directory-section h3 {
		margin: 0 0 15px 0;
		font-size: 14px;
		color: #ddd;
		font-weight: normal;
	}

	.episode-grid {
		display: grid;
		grid-template-columns: repeat(6, 1fr);
		gap: 8px;
	}

	.episode-item {
		aspect-ratio: 1;
		background-color: #2a2a2a;
		color: #aaa;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 4px;
		font-size: 14px;
		cursor: pointer;
		transition: all 0.2s;
		position: relative;
	}

	.episode-item:hover {
		background-color: #333;
		color: #fff;
	}

	.episode-item.active {
		border: 1px solid #ff9900;
		color: #ff9900;
		background-color: transparent;
	}

	.lock-icon {
		position: absolute;
		top: 0px;
		right: 0px;
		width: 12px;
		height: 12px;
		object-fit: contain;
	}

	/* Playing Animation */
	.playing-bars {
		display: flex;
		align-items: flex-end;
		gap: 2px;
		height: 12px;
		padding-bottom: 2px;
	}

	.playing-bars span {
		width: 2px;
		background-color: #ff9900;
		animation: equalize 1s infinite;
	}

	.playing-bars span:nth-child(1) {
		height: 6px;
		animation-delay: 0.1s;
	}

	.playing-bars span:nth-child(2) {
		height: 10px;
		animation-delay: 0.2s;
	}

	.playing-bars span:nth-child(3) {
		height: 8px;
		animation-delay: 0.3s;
	}

	@keyframes equalize {

		0%,
		100% {
			height: 4px;
		}

		50% {
			height: 12px;
		}
	}

	/* Recommendations Section */
	.recommend-section {
		padding: 20px;
		border-top: 1px solid #2a2a2a;
	}

	.section-divider {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 10px;
		margin-bottom: 15px;
	}

	.section-divider .line {
		width: 20px;
		height: 1px;
		background: linear-gradient(90deg, transparent, #ff9900);
	}

	.section-divider .line:last-child {
		background: linear-gradient(90deg, #ff9900, transparent);
	}

	.section-divider .text {
		font-size: 12px;
		color: #ddd;
	}

	.recommend-grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 10px;
	}

	.recommend-card {
		cursor: pointer;
	}

	.card-cover {
		width: 100%;
		aspect-ratio: 16/9;
		background-color: #333;
		background-size: cover;
		background-position: center;
		border-radius: 6px;
		position: relative;
		overflow: hidden;
		margin-bottom: 6px;
	}



	.card-info h4 {
		margin: 0 0 4px 0;
		font-size: 12px;
		color: #ddd;
		line-height: 1.3;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
		font-weight: normal;
	}

	.card-info p {
		margin: 0;
		font-size: 10px;
		color: #666;
	}

	/* Video Wrapper (Left Side) */
	.video-wrapper {
		flex: 1;
		display: flex;
		flex-direction: column;
		background-color: #000;
		border-radius: 8px;
		overflow: hidden;
		position: relative;
		/* Ensure fullscreen works */
	}

	.video-player {
		width: 100%;
		/* 固定播放器为 16:9 比例 */
		aspect-ratio: 16 / 9;
		position: relative;
		background-color: #000;
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		/* Hide overflow for controls */
	}

	.real-video {
		width: 100%;
		height: 100%;
		max-height: 100%;
		object-fit: contain;
	}

	/* Controls Overlay */
	.video-top-bar {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 80px;
		background: linear-gradient(to bottom, rgba(0, 0, 0, 0.7), transparent);
		padding: 20px;
		pointer-events: none;
		z-index: 10;
	}

	.video-title {
		margin: 0;
		color: #fff;
		font-size: 18px;
		font-weight: 500;
		text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
	}

	.video-controls-bar {
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		height: 60px;
		/* Adjust height as needed */
		background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
		padding: 0 20px;
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		z-index: 10;
		padding-bottom: 15px;
	}

	/* Progress Bar */
	.progress-container {
		width: 100%;
		height: 4px;
		background-color: rgba(255, 255, 255, 0.2);
		cursor: pointer;
		position: relative;
		margin-bottom: 10px;
		transition: height 0.1s;
		border-radius: 2px;
	}

	.progress-container:hover {
		height: 6px;
	}

	.progress-bg {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
	}

	.progress-buffered {
		position: absolute;
		top: 0;
		left: 0;
		bottom: 0;
		background-color: rgba(255, 255, 255, 0.4);
		border-radius: 2px;
	}

	.progress-current {
		position: absolute;
		top: 0;
		left: 0;
		bottom: 0;
		background-color: #ff9900;
		/* Theme color */
		border-radius: 2px;
		display: flex;
		align-items: center;
		justify-content: flex-end;
	}

	.progress-handle {
		width: 12px;
		height: 12px;
		background-color: #fff;
		border-radius: 50%;
		transform: translateX(50%) scale(0);
		transition: transform 0.1s;
	}

	.progress-container:hover .progress-handle {
		transform: translateX(50%) scale(1);
	}

	/* Controls Row */
	.controls-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		height: 30px;
	}

	.left-controls,
	.right-controls {
		display: flex;
		align-items: center;
		gap: 15px;
	}

	.control-btn {
		background: none;
		border: none;
		color: #ddd;
		cursor: pointer;
		padding: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		transition: color 0.2s;
	}

	.control-btn:hover {
		color: #fff;
	}

	.time-display {
		font-size: 12px;
		color: #ddd;
		font-variant-numeric: tabular-nums;
	}

	.control-text-btn {
		background: none;
		border: none;
		color: #ddd;
		cursor: pointer;
		font-size: 13px;
		padding: 0;
		transition: color 0.2s;
	}

	.control-text-btn:hover {
		color: #fff;
	}

	.control-text-btn.disabled {
		color: #666;
		cursor: not-allowed;
	}

	/* Volume Slider */
	.volume-control {
		position: relative;
		display: flex;
		align-items: center;
	}

	.volume-slider-container {
		position: absolute;
		bottom: 30px;
		left: 50%;
		transform: translateX(-50%);
		width: 30px;
		height: 100px;
		background-color: rgba(20, 20, 20, 0.9);
		border-radius: 4px;
		display: flex;
		justify-content: center;
		padding: 10px 0;
	}

	.volume-slider-bg {
		width: 4px;
		height: 100%;
		background-color: rgba(255, 255, 255, 0.2);
		position: relative;
		cursor: pointer;
		border-radius: 2px;
		display: flex;
		align-items: flex-end;
	}

	.volume-slider-current {
		width: 100%;
		background-color: #ff9900;
		border-radius: 2px;
	}

	/* Speed Menu */
	.speed-control {
		position: relative;
	}

	.speed-menu {
		position: absolute;
		bottom: 30px;
		left: 50%;
		transform: translateX(-50%);
		background-color: rgba(20, 20, 20, 0.9);
		border-radius: 4px;
		padding: 5px 0;
		display: flex;
		flex-direction: column;
		width: 60px;
		overflow: hidden;
	}

	.speed-item {
		padding: 8px 0;
		text-align: center;
		font-size: 12px;
		color: #ddd;
		cursor: pointer;
		transition: background-color 0.2s;
	}

	.speed-item:hover {
		background-color: rgba(255, 255, 255, 0.1);
		color: #fff;
	}

	.speed-item.active {
		color: #ff9900;
	}

	/* Loading Spinner */
	.loading-spinner {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		background-color: rgba(0, 0, 0, 0.5);
		z-index: 5;
	}

	.spinner {
		width: 40px;
		height: 40px;
		border: 4px solid rgba(255, 255, 255, 0.3);
		border-radius: 50%;
		border-top-color: #ff9900;
		animation: spin 1s ease-in-out infinite;
	}

	@keyframes spin {
		to {
			transform: rotate(360deg);
		}
	}

	/* Transition */
	.fade-enter-active,
	.fade-leave-active {
		transition: opacity 0.3s;
	}

	.fade-enter-from,
	.fade-leave-to {
		opacity: 0;
	}

	.video-footer {
		height: 60px;
		background-color: #1a1a1a;
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 0 20px;
	}

	.lecturer-info {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.lecturer-avatar {
		width: 32px;
		height: 32px;
		border-radius: 50%;
	}

	.lecturer-name {
		color: #fff;
		font-size: 14px;
	}

	/* Scrollbar */
	::-webkit-scrollbar {
		width: 6px;
	}

	::-webkit-scrollbar-track {
		background: #1a1a1a;
	}

	::-webkit-scrollbar-thumb {
		background: #333;
		border-radius: 3px;
	}

	::-webkit-scrollbar-thumb:hover {
		background: #444;
	}

	/* Member Overlay */
	.member-overlay {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(0, 0, 0, 0.85);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 20;
		backdrop-filter: blur(5px);
	}

	.overlay-content {
		text-align: center;
	}

	.overlay-content h3 {
		color: #fff;
		font-size: 20px;
		margin-bottom: 20px;
		font-weight: 500;
	}

	.vip-btn {
		background: linear-gradient(90deg, #FFC972 0%, #FFA73B 100%);
		border: none;
		border-radius: 20px;
		padding: 10px 30px;
		color: #522606;
		font-weight: bold;
		font-size: 14px;
		cursor: pointer;
		transition: transform 0.2s;
	}

	.vip-btn:hover {
		transform: scale(1.05);
	}

	/* Payment Modal Styles */
	.modal-overlay {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(0, 0, 0, 0.6);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 1000;
	}

	.payment-modal {
		background-color: #fff;
		width: 400px;
		border-radius: 12px;
		padding: 20px;
		color: #333;
		position: relative;
		animation: modalFadeIn 0.3s ease;
	}

	@keyframes modalFadeIn {
		from {
			opacity: 0;
			transform: translateY(20px);
		}

		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	.modal-header {
		display: flex;
		justify-content: center;
		align-items: center;
		margin-bottom: 20px;
		position: relative;
	}

	.modal-header h3 {
		margin: 0;
		font-size: 18px;
		font-weight: 500;
		color: #333;
	}

	.close-modal-btn {
		position: absolute;
		right: 0;
		top: 50%;
		transform: translateY(-50%);
		background: none;
		border: none;
		font-size: 24px;
		color: #999;
		cursor: pointer;
		padding: 0;
		line-height: 1;
	}

	.close-modal-btn:hover {
		color: #666;
	}

	.modal-product {
		display: flex;
		gap: 12px;
		margin-bottom: 20px;
	}

	.modal-cover {
		width: 120px;
		height: 68px;
		border-radius: 6px;
		object-fit: cover;
		background-color: #f5f5f5;
	}

	.modal-product-info {
		flex: 1;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
	}

	.modal-product-title {
		margin: 0;
		font-size: 14px;
		color: #333;
		line-height: 1.4;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	.modal-product-price {
		margin: 0;
		color: #ff9900;
		font-size: 16px;
		font-weight: 500;
	}

	.payment-methods {
		border: 1px solid #eee;
		border-radius: 8px;
		padding: 0 15px;
		margin-bottom: 25px;
	}

	.methods-label {
		font-size: 14px;
		color: #666;
		padding: 15px 0 10px;
	}

	.method-item {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 15px 0;
		cursor: pointer;
		border-top: 1px solid #eee;
	}

	.method-left {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.method-icon {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.method-name {
		font-size: 14px;
		color: #333;
	}

	.radio-circle {
		width: 18px;
		height: 18px;
		border-radius: 50%;
		border: 1px solid #ddd;
		display: flex;
		align-items: center;
		justify-content: center;
		transition: all 0.2s;
	}

	.radio-circle.checked {
		border-color: #ff9900;
		background-color: #ff9900;
	}

	.radio-inner {
		width: 8px;
		height: 8px;
		background: #fff;
		border-radius: 50%;
		/* Optional: use a checkmark icon instead for exact match with screenshot */
		mask: url('data:image/svg+xml;utf8,<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>') no-repeat center center;
		mask-size: contain;
		-webkit-mask: url('data:image/svg+xml;utf8,<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>') no-repeat center center;
		-webkit-mask-size: contain;
		background-color: #fff;
		width: 12px;
		height: 12px;
	}

	.confirm-pay-btn {
		width: 100%;
		background-color: #ff9900;
		color: #fff;
		border: none;
		border-radius: 4px;
		padding: 12px;
		font-size: 16px;
		cursor: pointer;
		transition: background-color 0.2s;
	}

	.confirm-pay-btn:hover {
		background-color: #e68a00;
	}

	/* WeChat QR Modal Styles (aligned with MyWallet.vue) */
	.qr-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		padding: 20px 0;
	}

	.qr-container img {
		width: 200px;
		height: 200px;
		margin-bottom: 15px;
	}

	.qr-container p {
		color: #666;
		font-size: 14px;
		margin: 0;
	}

	.loading {
		text-align: center;
		padding: 40px 0;
		color: #666;
	}

	.loading .spinner {
		display: inline-block;
		width: 24px;
		height: 24px;
		border: 3px solid rgba(0, 0, 0, 0.1);
		border-top-color: #409eff;
		border-radius: 50%;
		animation: spinner-rotate 0.8s linear infinite;
		margin-bottom: 10px;
	}

	.loading p {
		margin: 0;
	}

	/* 二维码生成 / 显示动画 */
	.qr-fade-enter-active,
	.qr-fade-leave-active {
		transition: opacity 0.3s ease, transform 0.3s ease;
	}

	.qr-fade-enter-from,
	.qr-fade-leave-to {
		opacity: 0;
		transform: scale(0.9);
	}

	.qr-fade-enter-to,
	.qr-fade-leave-from {
		opacity: 1;
		transform: scale(1);
	}

	@keyframes spinner-rotate {
		from {
			transform: rotate(0deg);
		}

		to {
			transform: rotate(360deg);
		}
	}

	/* Card Badges - Re-added to ensure visibility */
	.card-cover .badge.vip,
	.card-cover .badge.paid,
	.card-cover .badge.free {
		position: absolute;
		top: 0;
		right: 0;
		padding: 2px 4px;
		font-size: 10px;
		font-weight: bold;
		border-bottom-left-radius: 4px;
		z-index: 2;
		/* Ensure on top of cover image */
	}

	.card-cover .badge.vip {
		background: linear-gradient(90deg, #FFC972 0%, #FFA73B 100%);
		color: #522606;
	}

	.card-cover .badge.paid {
		background: linear-gradient(90deg, #FFC972 0%, #FFA73B 100%);
		color: #522606;
	}

	.card-cover .badge.free {
		background: rgba(0, 0, 0, 0.6);
		color: #FF9E27;
	}
</style>